import axios from "axios";
import { throttle } from "lodash";
import { Button } from 'primereact/button';
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from "primereact/datatable";
import { Dialog } from 'primereact/dialog';
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { MultiSelect } from "primereact/multiselect";
import React, { useRef, useState } from "react";
import { useQuery } from "react-query";
import PageHeading from "../../../components/PageHeading";
import PageLoadError from "../../../components/PageLoadError";
import PageLoading from "../../../components/PageLoading";
import { useAppContext } from "../../../context/appContext";
import { useUnclaimedProfileSwitchDialog } from "../../../hooks/useUnclaimedProfileSwitchDialog";
import authApi from "../../../service/IdApi";
import DataUtils from "../../../utils/DataUtils";

const filterOptions = [
    { name: 'Company Name', code: 'companyName' },
    { name: 'Published Status', code: 'published' },
    { name: 'Unclaimed Status', code: 'unclaimed' },
    { name: 'Spam Status', code: 'spam' },
    { name: 'Verified Status', code: 'verified' },
    { name: 'Featured Status', code: 'featured' },
    { name: 'Indexed Status', code: 'indexed' },
    { name: 'Pro Member Status', code: 'pro' },
    { name: 'Has Custom Domain', code: 'hasCustomDomain' }
];
const AllProfiles = () => {
    const { displayAlert } = useAppContext();
    const { switchUnclaimedProfile, UnclaimedProfileSwitchDialog } = useUnclaimedProfileSwitchDialog();
    const [profiles, setProfiles] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const dataTable = useRef(null);
    const [showVanityUrl, setShowVanityUrl] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const [showWebsite, setShowWebsite] = useState(false);
    const [showUpdatedOn, setShowUpdatedOn] = useState(false);
    const [showId, setShowId] = useState(false);
    const [showProviderId, setShowProviderId] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [showClaimYourProfileEmailDialog, setShowClaimYourProfileEmailDialog] = useState(false);
    const [claimYourProfileEmailSent, setClaimYourProfileEmailSent] = useState(false);
    const [emailsSent, setEmailsSent] = useState([]);
    const [totalEmailsSent, setTotalEmailsSent] = useState(0);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [rowActions, setRowActions] = useState([]);
    const [publishedFilter, setPublishedFilter] = useState("");
    const [spamFilter, setSpamFilter] = useState("");
    const [verifiedFilter, setVerifiedFilter] = useState("");
    const [featuredFilter, setFeaturedFilter] = useState("");
    const [indexedFilter, setIndexedFilter] = useState("");
    const [unclaimedFilter, setUnclaimedFilter] = useState("");
    const [proMemberFilter, setProMemberFilter] = useState("");
    const [hasCustomDomainFilter, setHasCustomDomainFilter] = useState("");
    const [filterByFields, setFilterByFields] = useState([{ name: 'Company Name', code: 'companyName' }]);

    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [removeCustomDomainDialog, setRemoveCustomDomainDialog] = useState(false);

    const [isRemovingCustomDomain, setIsRemovingCustomDomain] = useState(false);
    const [nameFilter, setNameFilter] = useState("");
    const [showIndexedState, setShowIndexedState] = useState(false);
    const [showMarkAsSpamDialog, setShowMarkAsSpamDialog] = useState(false);
    const [blockUserAndKillSessions, setBlockUserAndKillSessions] = useState(false);
    const [maskAsSpamLoading, setMaskAsSpamLoading] = useState(false);
    const [spamAction, setSpamAction] = useState(""); // "spam" or "unclaimed"

    const [users, setUsers] = useState([]);
    const menu = useRef(null);
    const [lazyState, setlazyState] = useState({
        first: 0,
        rows: 50,
        page: 1,
        sortField: "createdAt",
        sortOrder: 0,
    });

    // Custom domain invalidation dialog state
    const [showCustomDomainInvalidateDialog, setShowCustomDomainInvalidateDialog] = useState(false);
    const [customDomains, setCustomDomains] = useState([]);
    const [selectedCustomDomains, setSelectedCustomDomains] = useState([]);
    const [isLoadingCustomDomains, setIsLoadingCustomDomains] = useState(false);
    const [isInvalidating, setIsInvalidating] = useState(false);
    const [showAddCustomDomainDialog, setShowAddCustomDomainDialog] = useState(false);
    const [newCustomDomain, setNewCustomDomain] = useState("");
    const [isSearchingDomain, setIsSearchingDomain] = useState(false);
    const [domainSearchResult, setDomainSearchResult] = useState(null);

    // Add state for domain mode
    const [domainMode, setDomainMode] = useState("search"); // "search" or "existing"
    // WALL-661: existing-domain preflight (inspect) and purchase hosted-zone conflict flags
    const [existingDomainInspection, setExistingDomainInspection] = useState(null);
    const [isInspectingExistingDomain, setIsInspectingExistingDomain] = useState(false);

    // Add state for applying custom domain
    const [isApplyingCustomDomain, setIsApplyingCustomDomain] = useState(false);

    // Add state for deployment status
    const [deploymentStatus, setDeploymentStatus] = useState(null);
    const [deploymentMessage, setDeploymentMessage] = useState(null);
    const [isDeploying, setIsDeploying] = useState(false);
    const statusIntervalRef = useRef(null);

    // Add state for deployment logs
    const [deploymentLogs, setDeploymentLogs] = useState(null);

    // Add state for deployment completion
    const [deploymentCompleted, setDeploymentCompleted] = useState(false);

    let params = "?first=" + lazyState.first + "&rows=" + lazyState.rows;
    if (lazyState.sortField) {
        params += "&sortField=" + lazyState.sortField;
        if (lazyState.sortOrder) {
            params += "&sortOrder=" + lazyState.sortOrder;
        }
    }

    const { isLoading, isFetching, isError, data, refetch } = useQuery(["Profiles", params], async () => {
        setSelectedProfile(null);
        var searchBody = {};
        searchBody.first = lazyState.first;
        searchBody.rows = lazyState.rows;
        searchBody.sortField = lazyState.sortField;
        searchBody.sortOrder = lazyState.sortOrder;
        if (filterByFields.some(filter => filter.code === "published") && publishedFilter !== "") {
            if (publishedFilter === "published") {
                searchBody.published = true;
            }
            else if (publishedFilter === "unpublished") {
                searchBody.published = false;
            }
        }
        if (filterByFields.some(filter => filter.code === "unclaimed") && unclaimedFilter !== "") {
            if (unclaimedFilter === "claimed") {
                searchBody.unclaimed = false;
            }
            else if (unclaimedFilter === "unclaimed") {
                searchBody.unclaimed = true;
            }
        }
        if (filterByFields.some(filter => filter.code === "spam") && spamFilter !== "") {
            if (spamFilter === "spam") {
                searchBody.untrusted = true;
            }
            else if (spamFilter === "not spam") {
                searchBody.untrusted = false;
            }
        }
        if (filterByFields.some(filter => filter.code === "verified") && verifiedFilter !== "") {
            if (verifiedFilter === "verified") {
                searchBody.verified = true;
            }
            else if (verifiedFilter === "not verified") {
                searchBody.verified = false;
            }
        }
        if (filterByFields.some(filter => filter.code === "featured") && featuredFilter !== "") {
            if (featuredFilter === "featured") {
                searchBody.spotlight = true;
            }
            else if (featuredFilter === "not featured") {
                searchBody.spotlight = false;
            }
        }
        if (filterByFields.some(filter => filter.code === "indexed") && indexedFilter !== "") {
            if (indexedFilter === "indexed") {
                searchBody.indexed = true;
            }
            else if (indexedFilter === "not indexed") {
                searchBody.indexed = false;
            }
        }
        if (filterByFields.some(filter => filter.code === "pro") && proMemberFilter !== "") {
            if (proMemberFilter !== "either") {
                searchBody.pro = Number(proMemberFilter);
            }
        }
        if (filterByFields.some(filter => filter.code === "hasCustomDomain") && hasCustomDomainFilter !== "") {
            if (hasCustomDomainFilter === "yes") {
                searchBody.hasCustomDomain = true;
            }
            else if (hasCustomDomainFilter === "no") {
                searchBody.hasCustomDomain = false;
            }
        }
        if (filterByFields.some(filter => filter.code === "companyName") && nameFilter !== "") {
            searchBody.name = nameFilter;
        }
        const result = await authApi.post("admin/profile/list", searchBody);
        setProfiles(result.data.profiles);
        setTotalRecords(result.data.totalRecords);

        if (showIndexedState) {
            for (var i = 0; i < result.data.profiles.length; i++) {
                const profile = result.data.profiles[i];
                try {
                    const searchParameters = {
                        q: profile.vanityName,
                        query_by: 'vanityName',
                        per_page: 1,
                        filter_by: `vanityName:="${profile.vanityName}"`,
                        prefix: false,
                        typo_tolerance: 0,
                        match_threshold: 100,
                        infix: 'off'
                    };

                    const searchResults = await axios({
                        method: 'get',
                        url: `https://${process.env.REACT_APP_TYPESENSE_HOST}/collections/${process.env.REACT_APP_TYPESENSE_COMPANIES_COLLECTION}/documents/search`,
                        params: searchParameters,
                        headers: {
                            'X-TYPESENSE-API-KEY': process.env.REACT_APP_TYPESENSE_API_KEY,
                            'Content-Type': 'application/json'
                        }
                    });

                    let indexedState = "";
                    if (searchResults.data?.hits?.length > 0) {
                        if (profile.indexed) {
                            indexedState = "Good";
                        }
                        else {
                            indexedState = "Bad";
                        }
                    }
                    else {
                        if (profile.indexed) {
                            indexedState = "Bad";
                        }
                        else {
                            indexedState = "Good";
                        }
                    }
                    profile.indexedState = indexedState;
                } catch (error) {
                    console.error('Typesense search error:', error);
                }
            }
        }
        setLoaded(true);
        return data;
    });

    const openNew = () => {
        window.location.href = process.env.REACT_APP_NETWORK_SITE + "/company/create-profile";
    };

    const invalidateCloudfrontDistributions = async () => {
        setShowCustomDomainInvalidateDialog(true);
        setIsLoadingCustomDomains(true);
        setSelectedCustomDomains([]);

        try {
            const response = await authApi.get("admin/profile/custom-domain");
            // New structure: response.data.data is an array of objects
            const domains = response.data?.data || [];
            // Filter out entries with empty customDomain
            setCustomDomains(domains.filter(d => d.customDomain && d.customDomain.trim() !== ""));
        } catch (error) {
            displayAlert("error", "Error", "Failed to load custom domains");
            setShowCustomDomainInvalidateDialog(false);
        } finally {
            setIsLoadingCustomDomains(false);
        }
    };

    const pageHeader = (
        <PageHeading pageTitle="Admin - All Profiles">
            <div className="block">
                <Button label="Invalidate Cloudfront Distributions" icon="pi pi-refresh" className="mr-2" onClick={invalidateCloudfrontDistributions} />
            </div>
            <div className="block">
                <Button label="Create Unclaimed Profile" icon="pi pi-plus" className="mr-2" onClick={openNew} />
            </div>
        </PageHeading>
    );

    if (isLoading) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    if (!loaded) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    const onPage = (event) => {
        setlazyState(event);
    };

    const onSort = (event) => {
        setlazyState(event);
    };

    const exportCSV = (selectionOnly) => {
        dataTable.current.exportCSV({ selectionOnly });
    };

    const confirmUnclaimedEmailAction = throttle(async (profile) => {
        setShowClaimYourProfileEmailDialog(true);
        try {
            const response = await authApi.get("admin/profile/email/" + profile.providerId);
            setEmailsSent(response.data.emails);
            setTotalEmailsSent(response.data.totalRecords);
            for (var i = 0; i < response.data.totalRecords; i++) {
                if (response.data.emails[i].type === "new-unclaimed") {
                    setClaimYourProfileEmailSent(true);
                    break;
                }
            }
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }

    }, 500, { trailing: false })

    const hideClaimYourProfileEmailDialog = () => {
        setShowClaimYourProfileEmailDialog(false);
        setClaimYourProfileEmailSent(false);
        setEmailsSent([]);
        setTotalEmailsSent(0);
    }

    const sendClaimYourProfileEmailAction = throttle(async () => {
        setIsSendingEmail(true);
        var body = {};
        body.providerId = selectedProfile.providerId;
        body.type = "new-unclaimed";
        try {
            await authApi.post("admin/profile/email", body);
            setShowClaimYourProfileEmailDialog(false);
            displayAlert("success", "Success", "Email has been sent");
            setIsSendingEmail(false);
            setSelectedProfile(null);
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            setIsSendingEmail(false);
            throw error;
        }
    }, 500, { trailing: false });

    const markAsFeaturedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/spotlight", body);
            displayAlert("success", "Success", "Profile is now featured");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const unmarkAsFeaturedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/unspotlight", body);
            displayAlert("success", "Success", "Profile is no longer featured");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const indexAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/index", body);
            displayAlert("success", "Success", "Profile is now indexed");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const unindexAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/unindex", body);
            displayAlert("success", "Success", "Profile is no longer indexed");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const markAsVerifiedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/verified", body);
            displayAlert("success", "Success", "Profile is now verified");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const unmarkAsVerifiedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/unverified", body);
            displayAlert("success", "Success", "Profile is no longer verified");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const markAsUntrustedConfirmAction = throttle(async () => {
        setMaskAsSpamLoading(true);
        var body = {};
        body.providerId = selectedProfile.providerId;
        body.blockUser = blockUserAndKillSessions;

        try {
            if (spamAction === "spam") {
                await authApi.post("admin/profile/untrusted", body);
                const message = blockUserAndKillSessions ?
                    "Profile is now marked as spam and user account has been disabled" :
                    "Profile is now marked as spam";
                displayAlert("success", "Success", message);
            } else if (spamAction === "unclaimed") {
                await authApi.post("admin/profile/convert-to-unclaimed", body);
                const message = blockUserAndKillSessions ?
                    "Profile has been converted to unclaimed and user account has been disabled" :
                    "Profile has been converted to unclaimed";
                displayAlert("success", "Success", message);
            }
            setSelectedProfile(null);
            setShowMarkAsSpamDialog(false);
            setBlockUserAndKillSessions(false);
            setSpamAction("");
            setMaskAsSpamLoading(false);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            setMaskAsSpamLoading(false);
            throw error;
        }
    }, 500, { trailing: false });

    const markAsUntrustedAction = throttle(async (profile) => {
        setSelectedProfile(profile);
        setBlockUserAndKillSessions(false);
        setSpamAction("");
        setShowMarkAsSpamDialog(true);
    }, 500, { trailing: false });

    const unmarkAsUntrustedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/trusted", body);
            displayAlert("success", "Success", "Profile is no longer untrusted");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const updateRequestFormStatusAction = throttle(async (providerId, disableRequestForms) => {
        var body = {};
        body.providerId = providerId;
        body.disableRequestForms = disableRequestForms;
        try {
            await authApi.post("admin/profile/update-request-form-status", body);
            displayAlert("success", "Success", "Request forms status updated");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const removeCustomDomain = throttle(async () => {
        setIsRemovingCustomDomain(true);
        try {
            await authApi.post("admin/profile/custom-domain/remove", {
                providerId: selectedProfile.providerId,
                domain: selectedProfile.customDomain
            });
            displayAlert("success", "Success", "Custom domain removed successfully");
            setSelectedProfile(null);
            setRemoveCustomDomainDialog(false);
            refetch();
        } catch (error) {
            console.error("Remove custom domain error:", error);
            displayAlert("error", "Error", "Failed to remove custom domain: " + (error.response?.data?.message || error.message || "Unknown error"));
        } finally {
            setIsRemovingCustomDomain(false);
        }
    }, 500, { trailing: false });

    const showActionMenu = (e, rowData) => {
        const sendUnclaimedEmail = {
            label: "Send Claim Your Profile Email",
            icon: "pi pi-envelope",
            command: () => {
                confirmUnclaimedEmailAction(rowData);
            },
        };
        const markAsFeatured = {
            label: "Feature this profile",
            icon: "pi pi-bolt",
            command: () => {
                markAsFeaturedAction(rowData);
            },
        };
        const unmarkAsFeatured = {
            label: "Remove as featured profile",
            icon: "pi pi-times",
            command: () => {
                unmarkAsFeaturedAction(rowData);
            },
        };
        const index = {
            label: "Index this profile",
            icon: "pi pi-search-plus",
            command: () => {
                indexAction(rowData);
            },
        };
        const unindex = {
            label: "Remove as indexed profile",
            icon: "pi pi-search-minus",
            command: () => {
                unindexAction(rowData);
            },
        };
        const markAsVerified = {
            label: "Mark as verified",
            icon: "pi pi-thumbs-up",
            command: () => {
                markAsVerifiedAction(rowData);
            },
        };
        const unmarkAsVerified = {
            label: "Mark as unverified",
            icon: "pi pi-thumbs-down",
            command: () => {
                unmarkAsVerifiedAction(rowData);
            },
        };
        const markAsTrusted = {
            label: "Mark as spam and hide this profile",
            icon: "pi pi-eye-slash",
            command: () => {
                markAsUntrustedAction(rowData);
            },
        };
        const unmarkAsTrusted = {
            label: "Unmark as spam and show this profile",
            icon: "pi pi-eye",
            command: () => {
                confirmDialog({
                    message: "Are you sure you want to unmark as spam?",
                    header: "Confirm",
                    icon: "pi pi-exclamation-triangle",
                    rejectLabel: "Cancel",
                    rejectClassName: "p-button-danger p-button-text",
                    rejectIcon: "pi pi-times",
                    acceptIcon: "pi pi-check",
                    accept: () => {
                        unmarkAsUntrustedAction(rowData);
                    },
                });
            },
        };
        const currentlyDeploying = {
            label: "Currently Deploying...",
            icon: "pi pi-spin pi-spinner",
            command: () => {
                // Relaunch the dialog in deployment monitoring state
                setShowAddCustomDomainDialog(true);
                // Set the domain name to the one being deployed
                setNewCustomDomain(rowData.customDomain || "");
                // Set the deployment state to match an ongoing deployment
                setIsDeploying(true);
                setDeploymentStatus("PROCESSING");
                setDeploymentMessage("Deployment in progress...");
                setDeploymentCompleted(false);
                // Start polling for status updates
                startDeploymentMonitoring(rowData.providerId, rowData.customDomain);
            },
        };
        const removeCustomDomain = {
            label: "Remove Custom Domain",
            icon: "pi pi-trash",
            command: () => {
                setRemoveCustomDomainDialog(true);
            },
        };
        const addCustomDomain = {
            label: "Add Custom Domain",
            icon: "pi pi-plus",
            command: () => {
                if (!rowData?.providerId) {
                    displayAlert("error", "Error", "Invalid profile data. Please try again.");
                    return;
                }
                setShowAddCustomDomainDialog(true);
            },
        };
        const disableRequestForms = {
            label: "Disable Request Forms",
            icon: "pi pi-ban",
            command: () => {
                confirmDialog({
                    message: "Are you sure you want to disable request forms?",
                    header: "Confirm",
                    icon: "pi pi-exclamation-triangle",
                    rejectLabel: "Cancel",
                    rejectClassName: "p-button-danger p-button-text",
                    rejectIcon: "pi pi-times",
                    acceptIcon: "pi pi-check",
                    accept: () => {
                        updateRequestFormStatusAction(rowData.providerId, true);
                    },
                });
            },
        };

        const enableRequestForms = {
            label: "Enable Request Forms",
            icon: "pi pi-check",
            command: () => {
                confirmDialog({
                    message: "Are you sure you want to enable request forms?",
                    header: "Confirm",
                    icon: "pi pi-exclamation-triangle",
                    rejectLabel: "Cancel",
                    rejectClassName: "p-button-danger p-button-text",
                    rejectIcon: "pi pi-times",
                    acceptIcon: "pi pi-check",
                    accept: () => {
                        updateRequestFormStatusAction(rowData.providerId, false);
                    }
                });
            },
        };

        var _rowActions = []
        if (rowData.unclaimed && rowData.published) {
            _rowActions.push(sendUnclaimedEmail);
        }
        if (!rowData.spotlight) {
            _rowActions.push(markAsFeatured);
        }
        else {
            _rowActions.push(unmarkAsFeatured);
        }
        if (!rowData.indexed) {
            _rowActions.push(index);
        }
        else {
            _rowActions.push(unindex);
        }
        if (!rowData.verified) {
            _rowActions.push(markAsVerified);
        }
        else {
            _rowActions.push(unmarkAsVerified);
        }
        if (!rowData.untrusted) {
            _rowActions.push(markAsTrusted);
        }
        else {
            _rowActions.push(unmarkAsTrusted);
        }
        if (rowData.disableRequestForms) {
            _rowActions.push(enableRequestForms);
        }
        else {
            _rowActions.push(disableRequestForms);
        }
        if (rowData.deployingCustomDomain) {
            _rowActions.push(currentlyDeploying);
        } else if (DataUtils.isEmpty(rowData.customDomain)) {
            _rowActions.push(addCustomDomain);
        } else {
            _rowActions.push(removeCustomDomain);
        }

        setRowActions(_rowActions);
        setSelectedProfile(rowData);
        menu.current.toggle(e);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="Profile Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    const nameClicked = throttle(async (e) => {
        e.preventDefault();
        switchUnclaimedProfile(selectedProfile.providerId);
    }, 500, { trailing: false });

    const rowClicked = async (e, rowData) => {
        let manualClaimCode = null;
        if (rowData.unclaimed) {
            const detailsResult = await authApi.post("admin/provider/details?providerId=" + rowData.providerId);
            if (detailsResult.data.profile.manualClaimCode) {
                manualClaimCode = detailsResult.data.profile.manualClaimCode;
            }
        }
        setSelectedProfile({ ...rowData, manualClaimCode });
        var body = {};
        body.providerId = rowData.providerId;
        const usersResult = await authApi.post("admin/provider/users", body);
        setUsers(usersResult.data.users);
        setShowDetailsDialog(true);
    }

    const hideDetailsDialog = () => {
        setShowDetailsDialog(false);
    }

    const hideRemoveCustomDomainDialog = () => {
        setRemoveCustomDomainDialog(false);
    }

    const hideCustomDomainInvalidateDialog = () => {
        setShowCustomDomainInvalidateDialog(false);
        setCustomDomains([]);
        setSelectedCustomDomains([]);
    };

    const hideAddCustomDomainDialog = () => {
        setShowAddCustomDomainDialog(false);
        setNewCustomDomain("");
        setDomainSearchResult(null);
        setExistingDomainInspection(null);
        setDomainMode("search");
        setDeploymentStatus(null);
        setDeploymentMessage(null);
        setDeploymentLogs(null);
        setIsDeploying(false);
        setDeploymentCompleted(false); // Reset completion state

        // Stop all polling immediately when dialog is closed
        if (statusIntervalRef.current) {
            clearInterval(statusIntervalRef.current);
            statusIntervalRef.current = null;
        }
        // Reset selected profile to prevent any lingering state
        setSelectedProfile(null);
    };

    const selectAllCustomDomains = () => {
        setSelectedCustomDomains([...customDomains]);
    };

    const deselectAllCustomDomains = () => {
        setSelectedCustomDomains([]);
    };

    const handleCustomDomainSelection = (domainObj, checked) => {
        if (checked) {
            setSelectedCustomDomains([...selectedCustomDomains, domainObj]);
        } else {
            setSelectedCustomDomains(selectedCustomDomains.filter(d => d.customDomain !== domainObj.customDomain));
        }
    };

    const invalidateSelectedCustomDomains = async () => {
        if (selectedCustomDomains.length === 0) {
            displayAlert("warning", "Warning", "Please select at least one custom domain to invalidate");
            return;
        }

        setIsInvalidating(true);
        try {
            // Send only the customDomain strings to the backend
            await authApi.post("admin/profile/custom-domain/invalidate", selectedCustomDomains.map(d => d.customDomain));
            displayAlert("success", "Success", `${selectedCustomDomains.length} custom domain(s) invalidated successfully`);
            hideCustomDomainInvalidateDialog();
        } catch (error) {
            console.error("Invalidation error:", error);
            displayAlert("error", "Error", "Failed to invalidate custom domains: " + (error.response?.data?.message || error.message || "Unknown error"));
        } finally {
            setIsInvalidating(false);
        }
    };

    const searchCustomDomain = async (domainOverride = null) => {
        const domainToSearch = (domainOverride || newCustomDomain || "").trim();
        if (!domainToSearch) {
            displayAlert("warning", "Warning", "Please enter a domain name");
            return;
        }

        setIsSearchingDomain(true);
        setDomainSearchResult(null);
        try {
            const response = await authApi.post("admin/profile/custom-domain/search", { domain: domainToSearch });
            setDomainSearchResult(response.data);
        } catch (error) {
            console.error("Domain search error:", error);
            displayAlert("error", "Error", "Failed to check domain availability: " + (error.response?.data?.message || error.message || "Unknown error"));
        } finally {
            setIsSearchingDomain(false);
        }
    };

    // Inspect Route 53 registration + hosted-zone/NS state before enabling deploy (Add Existing Domain).
    const inspectExistingDomain = async () => {
        if (!newCustomDomain.trim()) {
            displayAlert("warning", "Warning", "Please enter a domain name");
            return;
        }

        setIsInspectingExistingDomain(true);
        setExistingDomainInspection(null);
        try {
            const response = await authApi.post("admin/profile/custom-domain/inspect-existing", {
                domain: newCustomDomain.trim()
            });
            setExistingDomainInspection(response.data);
        } catch (error) {
            console.error("Existing domain inspect error:", error);
            displayAlert("error", "Error", "Failed to inspect domain: " + (error.response?.data?.message || error.message || "Unknown error"));
        } finally {
            setIsInspectingExistingDomain(false);
        }
    };

    // Enforce WALL-661 UI gates: purchase blocked if a zone already exists; existing deploy only
    // when inspect returned deployAllowed (ready / ns_mismatch / will_create).
    const canApplyCustomDomain = () => {
        if (!newCustomDomain.trim() || !selectedProfile?.providerId || isDeploying) {
            return false;
        }
        if (domainMode === "search") {
            if (!domainSearchResult) {
                return false;
            }
            // RegisterDomain would create another zone beside any pre-existing one
            if (domainSearchResult.purchaseBlockedByHostedZone) {
                return false;
            }
            if (!domainSearchResult.available) {
                return false;
            }
            // Search result must match the currently selected domain (e.g. after picking a suggestion)
            if (domainSearchResult.info?.domainName
                && domainSearchResult.info.domainName.toLowerCase() !== newCustomDomain.trim().toLowerCase()) {
                return false;
            }
            return true;
        }
        if (domainMode === "existing") {
            if (!existingDomainInspection) {
                return false;
            }
            return existingDomainInspection.deployAllowed === true;
        }
        return false;
    };

    const checkDeploymentStatus = async (domainOverride = null, providerIdOverride = null) => {
        // Don't check status if deployment is already completed
        if (deploymentCompleted) {
            console.log("Deployment already completed, skipping status check");
            return;
        }

        const domainToCheck = domainOverride || newCustomDomain;
        const providerIdToUse = providerIdOverride || selectedProfile?.providerId;

        if (!domainToCheck.trim()) {
            console.warn("No custom domain to check status for");
            return;
        }

        if (!providerIdToUse) {
            console.warn("No profile selected for deployment status check");
            return;
        }

        try {
            console.log("Checking deployment status for:", domainToCheck.trim(), "providerId:", providerIdToUse);
            const response = await authApi.post("admin/profile/custom-domain/deployment-status", {
                domain: domainToCheck.trim(),
                providerId: providerIdToUse
            });
            setDeploymentStatus(response.data.status);
            setDeploymentMessage(response.data.message);

            // If status is COMPLETED or FAILED, also get the logs
            if (response.data.status === "COMPLETED" || response.data.status === "FAILED") {
                if (response.data.logs) {
                    setDeploymentLogs(response.data.logs);
                }
                // Clear the interval immediately using the ref
                if (statusIntervalRef.current) {
                    clearInterval(statusIntervalRef.current);
                    statusIntervalRef.current = null;
                }
                setIsDeploying(false);
                setDeploymentCompleted(true); // Mark as completed
                return; // Exit early to prevent further processing
            }
        } catch (error) {
            console.error("Error checking deployment status:", error);
            setDeploymentStatus("ERROR");
            setDeploymentMessage("Error checking status: " + (error.response?.data?.message || error.message));
            // Also clear interval on error using the ref
            if (statusIntervalRef.current) {
                clearInterval(statusIntervalRef.current);
                statusIntervalRef.current = null;
            }
            setIsDeploying(false);
            setDeploymentCompleted(true); // Mark as completed on error too
        }
    };

    const startDeploymentMonitoring = async (providerId, domain) => {
        console.log("Starting deployment monitoring for:", providerId, domain);

        // Set the selected profile for monitoring with all required fields
        const profileData = {
            providerId,
            customDomain: domain,
            name: "Monitoring Profile" // Add a name to prevent validation issues
        };
        setSelectedProfile(profileData);

        // Set the domain name for monitoring
        setNewCustomDomain(domain);

        // Clear any existing intervals first
        if (statusIntervalRef.current) {
            clearInterval(statusIntervalRef.current);
            statusIntervalRef.current = null;
        }

        // Reset completion state to allow monitoring
        setDeploymentCompleted(false);

        // Check status immediately first
        await checkDeploymentStatus(domain, providerId);

        // Only create the interval if deployment is NOT completed
        if (!deploymentCompleted) {
            const intervalId = setInterval(() => checkDeploymentStatus(domain, providerId), 5000);
            statusIntervalRef.current = intervalId;
        }
    };

    const applyCustomDomain = throttle(async () => {
        if (!newCustomDomain.trim()) {
            displayAlert("warning", "Warning", "Please enter a domain name");
            return;
        }

        if (!selectedProfile?.providerId) {
            displayAlert("error", "Error", "No profile selected. Please select a profile first.");
            return;
        }

        setIsApplyingCustomDomain(true);
        try {
            var body = {};
            body.providerId = selectedProfile.providerId;
            body.domain = newCustomDomain.trim();
            body.domainMode = domainMode;

            await authApi.post("admin/profile/custom-domain/deploy", body);
            displayAlert("success", "Success", "Custom domain applied successfully. Deployment in progress...");

            // Reset completion state
            setDeploymentCompleted(false);

            // Start deployment monitoring
            setIsDeploying(true);
            setDeploymentStatus("Starting deployment...");

            // Clear any existing interval first
            if (statusIntervalRef.current) {
                clearInterval(statusIntervalRef.current);
                statusIntervalRef.current = null;
            }

            // Check status immediately first
            await checkDeploymentStatus();

            // Only create the interval if deployment is NOT completed
            if (!deploymentCompleted) {
                const intervalId = setInterval(checkDeploymentStatus, 5000);
                statusIntervalRef.current = intervalId;
            }

        } catch (error) {
            displayAlert("error", "Error", "Failed to apply custom domain: " + (error.response?.data?.message || error.message || "Unknown error"));
        } finally {
            setIsApplyingCustomDomain(false);
        }
    }, 500, { trailing: false });

    const nameBodyTemplate = (rowData) => {
        return (
            <div className="">
                <a href="#" onClick={(e) => rowClicked(e, rowData)}>
                    {rowData.name}
                </a>
                {/* {!rowData.published && rowData.unclaimed && (
                    <a href="#" onClick={(e) => nameClicked(e, rowData)}>{rowData.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} /></a>
                )}
                {!rowData.published && !rowData.unclaimed && (
                    <a href="#" onClick={(e) => nameClicked(e, rowData)}>{rowData.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} /></a>
                )}
                {rowData.published && (
                    <a href="#" onClick={(e) => nameClicked(e, rowData)}>{rowData.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} /></a>
                )} */}
            </div>
        );
    };

    const unclaimedBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.unclaimed && (
                    <span>No</span>
                )}
                {!rowData.unclaimed && (
                    <span>Yes</span>
                )}
            </div>
        );
    };

    const publishedBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.published && (
                    <span>Yes</span>
                )}
                {!rowData.published && (
                    <span>No</span>
                )}
            </div>
        );
    };

    const untrustedBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.untrusted && (
                    <span>Yes</span>
                )}
                {!rowData.untrusted && (
                    <span>No</span>
                )}
            </div>
        );
    };

    const verifiedBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.verified && (
                    <span>Yes</span>
                )}
                {!rowData.verified && (
                    <span>No</span>
                )}
            </div>
        );
    };

    const spotlightBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.spotlight && (
                    <span>Yes</span>
                )}
                {!rowData.spotlight && (
                    <span>No</span>
                )}
            </div>
        );
    };

    const requestFormsBodyTemplate = (rowData) => {
        return (
            <div>
                <span>{rowData.disableRequestForms ? "No" : "Yes"}</span>
            </div>
        );
    };

    const indexedBodyTemplate = (rowData) => {
        return (
            <div>
                <span>{rowData.indexed ? "Yes" : "No"}</span>
            </div>
        )
    };

    const indexedStateBodyTemplate = (rowData) => {
        return (
            <div className={`${rowData.indexedState === "bad" ? "text-red-500" : ""}`}>{rowData.indexedState}</div>
        );
    };

    const proBodyTemplate = (rowData) => {
        return (
            <>
                <span>
                    {rowData.pro === 0 ? 'No' : rowData.pro === 1 ? 'Free Trial' : rowData.pro === 2 ? 'Active Pro' : ''}
                </span>
            </>
        );
    };

    const handleSearch = () => {
        refetch();
    };

    const emailBodyTemplate = (rowData) => {
        return (
            <div>
                <a href={`/account/admin/user-events?email=${rowData.email}`}>
                    {rowData.email}
                </a>
            </div>
        );
    }

    const clearFilters = () => {
        setFilterByFields([]);
        setPublishedFilter("");
        setUnclaimedFilter("");
        setSpamFilter("");
        setVerifiedFilter("");
        setFeaturedFilter("");
        setIndexedFilter("");
        setProMemberFilter("");
        setHasCustomDomainFilter("");
        setNameFilter("");
        setTimeout(() => {
            refetch();
        }, 100);
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const hideMarkAsSpamDialog = () => {
        setShowMarkAsSpamDialog(false);
        setBlockUserAndKillSessions(false);
        setSpamAction("");
        setSelectedProfile(null);
    };

    return (
        <div>
            <ConfirmDialog />
            {UnclaimedProfileSwitchDialog}
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex py-3 w-full">
                        <div className="flex flex-row align-items-center">
                            <div className="flex mr-3 font-bold">
                                Show/Hide Columns
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">Vanity URL</span>
                                <InputSwitch checked={showVanityUrl} onChange={(e) => setShowVanityUrl(e.value)} />
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">Email</span>
                                <InputSwitch checked={showEmail} onChange={(e) => setShowEmail(e.value)} />
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">Website</span>
                                <InputSwitch checked={showWebsite} onChange={(e) => setShowWebsite(e.value)} />
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">Updated On</span>
                                <InputSwitch checked={showUpdatedOn} onChange={(e) => setShowUpdatedOn(e.value)} />
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">ID</span>
                                <InputSwitch checked={showId} onChange={(e) => setShowId(e.value)} />
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">Provider ID</span>
                                <InputSwitch checked={showProviderId} onChange={(e) => setShowProviderId(e.value)} />
                            </div>
                            <div className="flex align-items-center">
                                <span className="mr-1">Indexed State</span>
                                <InputSwitch checked={showIndexedState} onChange={(e) => setShowIndexedState(e.value)} />
                            </div>
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                            <Button type="button" icon="pi pi-file-export" onClick={() => exportCSV(false)} data-pr-tooltip="CSV" label="Export" />
                        </div>
                    </div>
                    <div className="flex pb-3 w-full">
                        <div className="flex flex-row flex-wrap align-items-center">
                            <div className="font-bold mr-3 mb-2">
                                Filter By
                            </div>
                            <div className="flex mr-3 mb-2">
                                <MultiSelect value={filterByFields} onChange={(e) => setFilterByFields(e.value)} options={filterOptions} optionLabel="name"
                                    maxSelectedLabels={3} />
                            </div>
                            {filterByFields.some(filter => filter.code === "companyName") && (
                                <div className="flex mr-3 mb-2">
                                    <InputText
                                        value={nameFilter}
                                        onChange={(e) => setNameFilter(e.target.value)}
                                        placeholder="Company Name"
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            )}
                            {filterByFields.some(filter => filter.code === "published") && (
                                <div className="flex mr-3 mb-2">
                                    <Dropdown
                                        value={publishedFilter}
                                        onChange={(e) => setPublishedFilter(e.value)}
                                        options={[
                                            { label: 'Published', value: 'published' },
                                            { label: 'Unpublished', value: 'unpublished' },
                                            { label: 'Either', value: 'either' }
                                        ]}
                                        placeholder="Published Status"
                                    />
                                </div>
                            )}
                            {filterByFields.some(filter => filter.code === "unclaimed") && (
                                <div className="flex mr-3 mb-2">
                                    <Dropdown
                                        value={unclaimedFilter}
                                        onChange={(e) => setUnclaimedFilter(e.value)}
                                        options={[
                                            { label: 'Claimed', value: 'claimed' },
                                            { label: 'Unclaimed', value: 'unclaimed' },
                                            { label: 'Either', value: 'either' },
                                        ]}
                                        placeholder="Claimed Status"
                                    />
                                </div>
                            )}
                            {filterByFields.some(filter => filter.code === "spam") && (
                                <div className="flex mr-3 mb-2">
                                    <Dropdown
                                        value={spamFilter}
                                        onChange={(e) => setSpamFilter(e.value)}
                                        options={[
                                            { label: 'Spam', value: 'spam' },
                                            { label: 'Not Spam', value: 'not spam' },
                                            { label: 'Either', value: 'either' },
                                        ]}
                                        placeholder="Spam Status"
                                    />
                                </div>
                            )}
                            {filterByFields.some(filter => filter.code === "verified") && (
                                <div className="flex mr-3 mb-2">
                                    <Dropdown
                                        value={verifiedFilter}
                                        onChange={(e) => setVerifiedFilter(e.value)}
                                        options={[
                                            { label: 'Verified', value: 'verified' },
                                            { label: 'Not Verified', value: 'not verified' },
                                            { label: 'Either', value: 'either' },
                                        ]}
                                        placeholder="Verified Status"
                                    />
                                </div>
                            )}
                            {filterByFields.some(filter => filter.code === "featured") && (
                                <div className="flex mr-3 mb-2">
                                    <Dropdown
                                        value={featuredFilter}
                                        onChange={(e) => setFeaturedFilter(e.value)}
                                        options={[
                                            { label: 'Featured', value: 'featured' },
                                            { label: 'Not Featured', value: 'not featured' },
                                            { label: 'Either', value: 'either' },
                                        ]}
                                        placeholder="Featured Status"
                                    />
                                </div>
                            )}
                            {filterByFields.some(filter => filter.code === "indexed") && (
                                <div className="flex mr-3 mb-2">
                                    <Dropdown
                                        value={indexedFilter}
                                        onChange={(e) => setIndexedFilter(e.value)}
                                        options={[
                                            { label: 'Indexed', value: 'indexed' },
                                            { label: 'Not Indexed', value: 'not indexed' },
                                            { label: 'Either', value: 'either' },
                                        ]}
                                        placeholder="Indexed Status"
                                    />
                                </div>
                            )}
                            {filterByFields.some(filter => filter.code === "pro") && (
                                <div className="flex mr-3 mb-2">
                                    <Dropdown
                                        value={proMemberFilter}
                                        onChange={(e) => setProMemberFilter(e.value)}
                                        options={[
                                            { label: 'Free Trial Pro', value: '1' },
                                            { label: 'Active Pro Member', value: '2' },
                                            { label: 'Not Pro Member', value: '0' },
                                            { label: 'Either', value: 'either' },
                                        ]}
                                        placeholder="Pro Member Status"
                                    />
                                </div>
                            )}
                            {filterByFields.some(filter => filter.code === "hasCustomDomain") && (
                                <div className="flex mr-3 mb-2">
                                    <Dropdown
                                        value={hasCustomDomainFilter}
                                        onChange={(e) => setHasCustomDomainFilter(e.value)}
                                        options={[
                                            { label: 'Yes', value: 'yes' },
                                            { label: 'No', value: 'no' },
                                            { label: 'Either', value: 'either' },
                                        ]}
                                        placeholder="Has Custom Domain"
                                    />
                                </div>
                            )}
                            {filterByFields.length > 0 && (
                                <>
                                    <div className="flex mb-2">
                                        <Button
                                            icon="pi pi-search"
                                            className="p-button-primary"
                                            onClick={handleSearch}
                                            tooltip="Search"
                                            loading={isLoading || isFetching}
                                            label="Search"
                                        />
                                    </div>
                                    <div className="flex mb-2">
                                        <Button onClick={clearFilters} className="p-button-text" icon="pi pi-times" tooltip="Clear Filter" />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                        </div>
                    </div>
                    <Menu model={rowActions} popup ref={menu} className="white-space-nowrap" style={{ width: "325px" }} />
                    <DataTable
                        ref={dataTable}
                        value={profiles}
                        // selection={selectedItems}
                        dataKey="id"
                        paginator
                        rows={lazyState.rows}
                        rowsPerPageOptions={[10, 25, 50, 100, 500]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} profiles"
                        emptyMessage="No profiles found."
                        // onRowSelect={onRowSelect}
                        lazy
                        first={lazyState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={lazyState.sortField}
                        sortOrder={lazyState.sortOrder}
                        loading={isLoading || !loaded}
                        selection={selectedProfile}
                        onSelectionChange={(e) => setSelectedProfile(e.value)}
                        selectionMode="single"
                    >
                        <Column body={actionBodyTemplate} headerStyle={{ width: "1%", minWidth: "1rem" }}></Column>
                        <Column body={nameBodyTemplate} field="name" header="Name" sortable />
                        {showVanityUrl && (
                            <Column field="vanityName" header="Vanity URL" sortable />
                        )}
                        {showEmail && (
                            <Column field="email" header="Email" sortable />
                        )}
                        {showWebsite && (
                            <Column field="website" header="Website" sortable />
                        )}
                        <Column body={publishedBodyTemplate} field="published" header="Published" sortable />
                        <Column body={unclaimedBodyTemplate} field="unclaimed" header="Claimed" sortable />
                        <Column body={untrustedBodyTemplate} field="untrusted" header="Spam" sortable />
                        <Column body={verifiedBodyTemplate} field="verified" header="Verified" sortable />
                        <Column body={spotlightBodyTemplate} field="spotlight" header="Featured" sortable />
                        <Column body={requestFormsBodyTemplate} field="disableRequestForms" header="Request Forms" sortable />
                        <Column body={indexedBodyTemplate} field="indexed" header="Indexed" sortable />
                        {showIndexedState && (
                            <Column body={indexedStateBodyTemplate} header="Indexed State" />
                        )}
                        <Column body={proBodyTemplate} field="pro" header="Pro Member" sortable />
                        <Column field="systemRating" header="System Rating" sortable />
                        <Column field="createdAt" header="Created On" sortable />
                        {showUpdatedOn && (
                            <Column field="updatedAt" header="Updated On" sortable />
                        )}
                        {showId && (
                            <Column field="id" header="ID" sortable />
                        )}
                        {showProviderId && (
                            <Column field="providerId" header="Provider ID" sortable />
                        )}
                    </DataTable>
                </div>
            </div>
            <div>
                <Dialog
                    visible={showClaimYourProfileEmailDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Send Claim Your Profile Email"
                    modal
                    onHide={hideClaimYourProfileEmailDialog}
                    className="p-fluid"
                >
                    <div className="flex flex-column">
                        <>
                            {claimYourProfileEmailSent && (
                                <div className="flex text-xl mb-3">
                                    <i className="pi pi-exclamation-triangle mr-2" style={{ fontSize: '1.4rem' }}></i>The claim your profile email was already sent.
                                </div>
                            )}
                            {selectedProfile && (
                                <div className="flex flex-wrap flex-column">
                                    <div className="flex mb-1">
                                        To send this unclaimed profile a message encouraging them to claim their profile, please click the link below.
                                    </div>
                                    <div className="flex justify-content-center">
                                        <Button onClick={() => sendClaimYourProfileEmailAction()} className="w-min white-space-nowrap" label={"Send email to " + selectedProfile.email} loading={isSendingEmail} />
                                    </div>
                                </div>
                            )}
                        </>
                    </div>
                    <div>
                        <hr />
                        <div className="font-bold text-lg mt-3">
                            Previous Emails Sent ({totalEmailsSent})
                        </div>
                        <div>
                            {totalEmailsSent === 0 && (
                                <div>No emails were sent</div>
                            )}
                        </div>
                        <div>
                            {emailsSent.map((email, index) =>
                                <div key={index} className=" mt-2">
                                    <div>
                                        From: {email.sender}
                                    </div>
                                    <div>
                                        To: {email.recipient}
                                    </div>
                                    <div>
                                        Date: {email.sentAt}
                                    </div>
                                    <div>
                                        Type: {email.type}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Dialog>
                <Dialog
                    visible={showDetailsDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Profile Details"
                    modal
                    onHide={hideDetailsDialog}
                    className="p-fluid"
                >
                    {selectedProfile && (
                        <div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Name</label>
                                <div className="col">
                                    <a href="#" onClick={(e) => nameClicked(e)}>
                                        {selectedProfile.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} />
                                    </a>
                                </div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Provider ID</label>
                                <div className="col">{selectedProfile.providerId}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Profile URL</label>
                                {selectedProfile.published && (
                                    <div className="col">
                                        <a href={process.env.REACT_APP_NETWORK_SITE + "/company/" + selectedProfile.vanityName} target="_blank">
                                            /company/{selectedProfile.vanityName}<span className="ml-2 pi pi-external-link" style={{ fontSize: '.8rem' }} />
                                        </a>
                                    </div>
                                )}
                                {!selectedProfile.published && (
                                    <div className="col">
                                        /company/{selectedProfile.vanityName}
                                    </div>
                                )}
                            </div>
                            <div>
                                <div className="field grid mb-0">
                                    <label className="col-fixed" style={{ width: '150px' }}>Custom Domain</label>
                                    <div className="col">{selectedProfile.customDomain}</div>
                                </div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>System Rating</label>
                                <div className="col">{selectedProfile.rating}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Published</label>
                                <div className="col">{selectedProfile.published ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Claimed</label>
                                <div className="col">{selectedProfile.unclaimed ? 'No' : 'Yes'}</div>
                            </div>
                            {selectedProfile.unclaimed && (
                                <div className="field grid mb-0">
                                    <label className="col-fixed" style={{ width: '150px' }}>Verification Code</label>
                                    <div className="col">{selectedProfile.manualClaimCode ? selectedProfile.manualClaimCode : 'Not set'}</div>
                                </div>
                            )}
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Spam</label>
                                <div className="col">{selectedProfile.untrusted ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Verified</label>
                                <div className="col">{selectedProfile.verified ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Featured</label>
                                <div className="col">{selectedProfile.spotlight ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Indexed</label>
                                <div className="col">{selectedProfile.indexed ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Pro Member</label>
                                <div className="col">{selectedProfile.pro ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Review Count</label>
                                <div className="col">{selectedProfile.reviewCount}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Created On</label>
                                <div className="col">{selectedProfile.createdAt}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Users</label>
                            </div>
                            <DataTable
                                value={users}
                                dataKey="userId"
                                className="datatable-responsive"
                            >
                                <Column field="userId" header="ID" sortable />
                                <Column field="email" body={emailBodyTemplate} header="Email" sortable />
                                <Column field="createdAt" header="Created On" sortable />
                            </DataTable>
                        </div>

                    )}
                </Dialog>
                <Dialog
                    visible={removeCustomDomainDialog}
                    style={{ width: '400px' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Remove Custom Domain"
                    modal
                    onHide={hideRemoveCustomDomainDialog}
                    className="p-fluid"
                >
                    {selectedProfile && selectedProfile.customDomain && (
                        <div className="flex flex-column">
                            <div className="mb-3">
                                <div className="flex align-items-center mb-2">
                                    <i className="pi pi-exclamation-triangle text-orange-500 mr-2"></i>
                                    <span className="font-medium">Confirm Domain Removal</span>
                                </div>
                                <p className="text-gray-600">
                                    Are you sure you want to remove the custom domain <strong>{selectedProfile.customDomain}</strong> from this profile?
                                </p>
                                <p className="text-sm text-gray-500 mt-2">
                                    This action will remove the custom domain configuration and cannot be undone.
                                </p>
                            </div>
                            <div className="flex flex-row justify-content-end">
                                <div className="flex mr-2">
                                    <Button label="Cancel" onClick={hideRemoveCustomDomainDialog} className="p-button-text" icon="pi pi-times" />
                                </div>
                                <div className="flex">
                                    <Button
                                        label="Remove Domain"
                                        onClick={removeCustomDomain}
                                        loading={isRemovingCustomDomain}
                                        className="p-button-danger"
                                        icon="pi pi-trash"
                                    />
                                </div>
                            </div>
                        </div>

                    )}
                </Dialog>
                <Dialog
                    visible={showMarkAsSpamDialog}
                    style={{ width: '500px' }}
                    breakpoints={{ '1280px': '500px', '641px': '95vw' }}
                    header="Confirm Mark as Spam"
                    modal
                    onHide={hideMarkAsSpamDialog}
                    className="p-fluid"
                >
                    {selectedProfile && (
                        <div className="flex flex-column">
                            <div className="mb-3 flex align-items-start">
                                <i className="pi pi-exclamation-triangle mr-3 mt-1" style={{ fontSize: '1.2rem', color: '#f59e0b' }}></i>
                                <div>
                                    You can convert this profile to be Unclaimed allowing it to be claimed by a real representative of the business, or you can remove visibility of the profile completely by marking it as Spam.
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="block mb-2 font-medium">Action to Perform</label>
                                <div className="ml-3">
                                    <div className="flex align-items-center mb-2">
                                        <input
                                            type="radio"
                                            id="spamAction"
                                            name="spamAction"
                                            value="spam"
                                            checked={spamAction === "spam"}
                                            onChange={(e) => setSpamAction(e.target.value)}
                                            className="mr-2"
                                        />
                                        <label htmlFor="spamAction" className="cursor-pointer">
                                            Mark as Spam and remove visibility to profile
                                        </label>
                                    </div>
                                    <div className="flex align-items-center">
                                        <input
                                            type="radio"
                                            id="unclaimedAction"
                                            name="spamAction"
                                            value="unclaimed"
                                            checked={spamAction === "unclaimed"}
                                            onChange={(e) => setSpamAction(e.target.value)}
                                            className="mr-2"
                                        />
                                        <label htmlFor="unclaimedAction" className="cursor-pointer">
                                            Convert to Unclaimed allowing others to claim it
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <div className="mb-2">
                                    If this profile was created by a bad actor, you can disable the user's access to Wallist as well.
                                </div>
                                <div className="ml-3 flex align-items-center">
                                    <Checkbox
                                        checked={blockUserAndKillSessions}
                                        onChange={(e) => setBlockUserAndKillSessions(e.checked)}
                                        inputId="blockUser"
                                        className="mr-2"
                                    />
                                    <label htmlFor="blockUser" className="cursor-pointer">
                                        Disable User and Kill Sessions
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-content-end">
                                <div>
                                    <Button
                                        label="Cancel"
                                        onClick={hideMarkAsSpamDialog}
                                        className="p-button-text p-button-danger mr-2"
                                        icon="pi pi-times"
                                    />
                                </div>
                                <div>
                                    <Button
                                        label="Confirm"
                                        onClick={markAsUntrustedConfirmAction}
                                        icon="pi pi-check"
                                        loading={maskAsSpamLoading}
                                        disabled={!spamAction}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </Dialog>
                <Dialog
                    visible={showCustomDomainInvalidateDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Invalidate Custom Domains"
                    modal
                    onHide={hideCustomDomainInvalidateDialog}
                    className="p-fluid"
                >
                    <div className="flex flex-column">
                        <div className="flex flex-column">
                            {isLoadingCustomDomains ? (
                                <div>Loading custom domains...</div>
                            ) : customDomains.length === 0 ? (
                                <div>No custom domains found</div>
                            ) : (
                                <div style={{ maxHeight: '400px', overflow: 'hidden' }}>
                                    <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                        <table className="w-full">
                                            <thead className="bg-gray-50" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                <tr>
                                                    <th className="text-left p-3 border-bottom-1 border-gray-200" style={{ width: '50px' }}>
                                                        <Checkbox
                                                            checked={selectedCustomDomains.length === customDomains.length && customDomains.length > 0}
                                                            onChange={(e) => e.checked ? selectAllCustomDomains() : deselectAllCustomDomains()}
                                                        />
                                                    </th>
                                                    <th className="text-left p-3 border-bottom-1 border-gray-200">Profile Name</th>
                                                    <th className="text-left p-3 border-bottom-1 border-gray-200">Custom Domain</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {customDomains.map((domain, index) => (
                                                    <tr key={index} className="border-bottom-1 border-gray-100">
                                                        <td className="p-3" style={{ width: '50px' }}>
                                                            <Checkbox
                                                                checked={selectedCustomDomains.some(d => d.customDomain === domain.customDomain)}
                                                                onChange={(e) => handleCustomDomainSelection(domain, e.checked)}
                                                            />
                                                        </td>
                                                        <td className="p-3">{domain.profileName}</td>
                                                        <td className="p-3 font-mono text-sm">{domain.customDomain}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-row justify-content-end mt-3">
                            <div className="flex mr-2">
                                <Button label="Cancel" onClick={hideCustomDomainInvalidateDialog} className="p-button-text p-button-danger" icon="pi pi-times" />
                            </div>
                            <div className="flex">
                                <Button
                                    label={`Invalidate Selected (${selectedCustomDomains.length})`}
                                    onClick={invalidateSelectedCustomDomains}
                                    loading={isInvalidating}
                                    icon="pi pi-refresh"
                                    disabled={selectedCustomDomains.length === 0}
                                />
                            </div>
                        </div>
                    </div>
                </Dialog>
                <Dialog
                    visible={showAddCustomDomainDialog}
                    style={{ width: '800px' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Add Custom Domain"
                    modal
                    onHide={hideAddCustomDomainDialog}
                    className="p-fluid"
                >
                    <div className="flex flex-column">
                        {/* Profile Validation */}
                        {!selectedProfile?.providerId && (
                            <div className="p-3 border-round mb-3 bg-red-50 border-red-200">
                                <div className="flex align-items-center">
                                    <i className="pi pi-exclamation-triangle text-red-600 mr-2"></i>
                                    <span className="text-red-700 font-medium">No profile selected. Please close this dialog and select a profile first.</span>
                                </div>
                            </div>
                        )}

                        {/* Domain Mode Selection */}
                        <div className="mb-3">
                            <label className="font-medium mb-2">Domain Type</label>
                            <div className="flex flex-column">
                                <div className="flex align-items-center mb-2">
                                    <input
                                        type="radio"
                                        id="searchMode"
                                        name="domainMode"
                                        value="search"
                                        checked={domainMode === "search"}
                                        onChange={(e) => {
                                            setDomainMode(e.target.value);
                                            setExistingDomainInspection(null);
                                            setDomainSearchResult(null);
                                        }}
                                        className="mr-2"
                                        disabled={isDeploying || deploymentStatus === "COMPLETED" || deploymentStatus === "FAILED"}
                                    />
                                    <label htmlFor="searchMode" className={`cursor-pointer ${(isDeploying || deploymentStatus === "COMPLETED" || deploymentStatus === "FAILED") ? "text-gray-400" : ""}`}>Search for New Domain</label>
                                </div>
                                <div className="flex align-items-center">
                                    <input
                                        type="radio"
                                        id="existingMode"
                                        name="domainMode"
                                        value="existing"
                                        checked={domainMode === "existing"}
                                        onChange={(e) => {
                                            setDomainMode(e.target.value);
                                            setExistingDomainInspection(null);
                                            setDomainSearchResult(null);
                                        }}
                                        className="mr-2"
                                        disabled={isDeploying || deploymentStatus === "COMPLETED" || deploymentStatus === "FAILED"}
                                    />
                                    <label htmlFor="existingMode" className={`cursor-pointer ${(isDeploying || deploymentStatus === "COMPLETED" || deploymentStatus === "FAILED") ? "text-gray-400" : ""}`}>Add Existing Domain</label>
                                </div>
                            </div>
                        </div>

                        {/* Search Mode Content */}
                        {domainMode === "search" && (
                            <>
                                <div className="mb-3">
                                    <label className="font-medium">Domain Name</label>
                                    <div className="flex flex-row align-items-center mt-1">
                                        <InputText
                                            value={newCustomDomain}
                                            onChange={(e) => setNewCustomDomain(e.target.value)}
                                            placeholder="Enter domain name (e.g., example.com)"
                                            className="flex-grow-1 mr-2"
                                            disabled={isDeploying || deploymentStatus === "COMPLETED" || deploymentStatus === "FAILED"}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    searchCustomDomain();
                                                }
                                            }}
                                        />
                                        <Button
                                            icon="pi pi-search"
                                            onClick={searchCustomDomain}
                                            loading={isSearchingDomain}
                                            tooltip="Check Availability"
                                            disabled={isDeploying || deploymentCompleted}
                                        />
                                    </div>
                                </div>

                                {domainSearchResult && (
                                    <div className="mb-3">
                                        {/* WALL-661: block purchase when a same-named hosted zone already exists
                                            (RegisterDomain would create a duplicate). */}
                                        {domainSearchResult.purchaseBlockedByHostedZone && (
                                            <div className="p-3 border-round mb-3 bg-red-50 border-red-200">
                                                <div className="flex align-items-center mb-2">
                                                    <i className="pi pi-times-circle text-red-600 mr-2"></i>
                                                    <span className="font-bold text-red-700">Cannot purchase — hosted zone already exists</span>
                                                </div>
                                                <div className="text-sm text-red-700 mb-2">
                                                    {domainSearchResult.purchaseBlockMessage}
                                                </div>
                                                {domainSearchResult.existingHostedZones?.length > 0 && (
                                                    <div className="text-sm text-gray-700">
                                                        <div className="font-medium mb-1">Existing hosted zone ID(s):</div>
                                                        <ul className="m-0 pl-3">
                                                            {domainSearchResult.existingHostedZones.map((zone) => (
                                                                <li key={zone.id} className="font-mono">{zone.id}</li>
                                                            ))}
                                                        </ul>
                                                        <div className="mt-2">Delete these in Route 53, then search again.</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Main Domain Info - Only show when NOT deploying */}
                                        {!isDeploying && !deploymentCompleted && !domainSearchResult.purchaseBlockedByHostedZone && (
                                            <div className={`p-3 border-round mb-3 ${domainSearchResult.available ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                                <div className="flex align-items-center mb-2">
                                                    <i className={`pi ${domainSearchResult.available ? 'pi-check-circle text-green-600' : 'pi-times-circle text-red-600'} mr-2`}></i>
                                                    <span className={`font-bold ${domainSearchResult.available ? 'text-green-700' : 'text-red-700'}`}>
                                                        {domainSearchResult.available ? 'Domain is available' : 'Domain is not available'}
                                                    </span>
                                                </div>

                                                {domainSearchResult.info && (
                                                    <div className="grid">
                                                        <div className="col-6">
                                                            <div className="text-sm font-medium text-gray-600">Domain Name:</div>
                                                            <div className="font-mono">{domainSearchResult.info.domainName}</div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="text-sm font-medium text-gray-600">TLD Price:</div>
                                                            <div>{domainSearchResult.available ? `$${domainSearchResult.info.price} ${domainSearchResult.info.currency}` : '-'}</div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="text-sm font-medium text-gray-600">TLD:</div>
                                                            <div className="uppercase">{domainSearchResult.info.tld}</div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="text-sm font-medium text-gray-600">Premium:</div>
                                                            <div>{domainSearchResult.info.premium ? 'Yes' : 'No'}</div>
                                                        </div>
                                                    </div>
                                                )}

                                                {domainSearchResult.message && (
                                                    <div className="mt-2 text-sm text-gray-600">
                                                        {domainSearchResult.message}
                                                    </div>
                                                )}

                                                {domainSearchResult.available && domainSearchResult.info?.domainName && (
                                                    <div className="mt-2">
                                                        <a
                                                            href={`https://www.godaddy.com/domainsearch/find?domainToCheck=${domainSearchResult.info.domainName}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:text-blue-800 underline text-sm"
                                                        >
                                                            <i className="pi pi-external-link mr-1"></i>
                                                            Spot Check Pricing on GoDaddy
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Suggestions Table - Only show when searched domain is NOT available AND deployment hasn't started */}
                                        {!domainSearchResult.available && !domainSearchResult.purchaseBlockedByHostedZone && domainSearchResult.info?.suggestions && domainSearchResult.info.suggestions.length > 0 && !isDeploying && !deploymentCompleted && (
                                            <div className="mb-3">
                                                <div className="font-bold text-lg mb-2">Domain Suggestions</div>
                                                <div className="text-sm text-gray-600 mb-2">
                                                    Select a suggested domain below to deploy instead:
                                                </div>
                                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                    <table className="w-full">
                                                        <thead className="bg-gray-50" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                            <tr>
                                                                <th className="text-left p-3 border-bottom-1 border-gray-200"></th>
                                                                <th className="text-left p-3 border-bottom-1 border-gray-200">Domain Name</th>
                                                                <th className="text-left p-3 border-bottom-1 border-gray-200">TLD</th>
                                                                <th className="text-left p-3 border-bottom-1 border-gray-200">TLD Price</th>
                                                                <th className="text-left p-3 border-bottom-1 border-gray-200">Premium</th>
                                                                <th className="text-left p-3 border-bottom-1 border-gray-200">Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {domainSearchResult.info.suggestions.map((suggestion, index) => (
                                                                <tr key={index} className="border-bottom-1 border-gray-100 hover:bg-gray-50">
                                                                    <td className="p-3" style={{ width: '50px' }}>
                                                                        <input
                                                                            type="radio"
                                                                            id={`suggestion-${index}`}
                                                                            name="selectedSuggestion"
                                                                            value={suggestion.domainName}
                                                                            onChange={(e) => {
                                                                                const selectedDomain = e.target.value;
                                                                                setNewCustomDomain(selectedDomain);
                                                                                searchCustomDomain(selectedDomain);
                                                                            }}
                                                                            className="mr-2"
                                                                        />
                                                                    </td>
                                                                    <td className="p-3 font-mono text-sm">{suggestion.domainName}</td>
                                                                    <td className="p-3 uppercase">{suggestion.tld}</td>
                                                                    <td className="p-3">${suggestion.price} {suggestion.currency}</td>
                                                                    <td className="p-3">{suggestion.premium ? 'Yes' : 'No'}</td>
                                                                    <td className="p-3">
                                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${suggestion.available
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : 'bg-red-100 text-red-800'
                                                                            }`}>
                                                                            {suggestion.available ? 'Available' : 'Unavailable'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Existing Domain Mode: must inspect registration + hosted-zone/NS state before Apply */}
                        {domainMode === "existing" && (
                            <div className="mb-3">
                                <label className="font-medium">Existing Domain Name</label>
                                <div className="flex flex-row align-items-center mt-1">
                                    <InputText
                                        value={newCustomDomain}
                                        onChange={(e) => {
                                            setNewCustomDomain(e.target.value);
                                            setExistingDomainInspection(null);
                                        }}
                                        placeholder="Enter your existing domain (e.g., example.com)"
                                        className="flex-grow-1 mr-2"
                                        disabled={isDeploying || deploymentStatus === "COMPLETED" || deploymentStatus === "FAILED"}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                inspectExistingDomain();
                                            }
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-search"
                                        onClick={inspectExistingDomain}
                                        loading={isInspectingExistingDomain}
                                        tooltip="Inspect Domain"
                                        disabled={isDeploying || deploymentCompleted}
                                    />
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                    Domain must already be registered in the Wallist Route 53 Domains account. Inspect before deploying.
                                </div>

                                {existingDomainInspection && (
                                    <div className={`p-3 border-round mt-3 ${
                                        existingDomainInspection.status === "ready" ? "bg-green-50 border-green-200" :
                                        existingDomainInspection.status === "ns_mismatch" || existingDomainInspection.status === "will_create" || existingDomainInspection.status === "inspection_error" ? "bg-yellow-50 border-yellow-200" :
                                        "bg-red-50 border-red-200"
                                    }`}>
                                        <div className="flex align-items-center mb-2">
                                            <i className={`pi mr-2 ${
                                                existingDomainInspection.status === "ready" ? "pi-check-circle text-green-600" :
                                                existingDomainInspection.status === "ns_mismatch" || existingDomainInspection.status === "will_create" || existingDomainInspection.status === "inspection_error" ? "pi-exclamation-triangle text-yellow-600" :
                                                "pi-times-circle text-red-600"
                                            }`}></i>
                                            <span className={`font-bold ${
                                                existingDomainInspection.status === "ready" ? "text-green-700" :
                                                existingDomainInspection.status === "ns_mismatch" || existingDomainInspection.status === "will_create" || existingDomainInspection.status === "inspection_error" ? "text-yellow-700" :
                                                "text-red-700"
                                            }`}>
                                                {existingDomainInspection.status === "ready" && "Ready to deploy"}
                                                {existingDomainInspection.status === "ns_mismatch" && "Name servers will be auto-updated"}
                                                {existingDomainInspection.status === "will_create" && "Hosted zone will be created"}
                                                {existingDomainInspection.status === "not_registered" && "Domain not in Wallist account"}
                                                {existingDomainInspection.status === "duplicate_zones" && "Duplicate hosted zones — cleanup required"}
                                                {existingDomainInspection.status === "inspection_error" && "Inspection failed — try again"}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-700 mb-2">
                                            {existingDomainInspection.message}
                                        </div>
                                        {existingDomainInspection.status === "inspection_error" && (
                                            <div className="text-sm text-yellow-800 mt-2">
                                                This is usually a temporary AWS error. Click Inspect again to retry.
                                            </div>
                                        )}
                                        {existingDomainInspection.hostedZones?.length > 0 && (
                                            <div className="text-sm text-gray-700">
                                                <div className="font-medium mb-1">Hosted zone(s):</div>
                                                <ul className="m-0 pl-3">
                                                    {existingDomainInspection.hostedZones.map((zone) => (
                                                        <li key={zone.id} className="font-mono">
                                                            {zone.id}
                                                            {zone.nsMatch ? " (NS match)" : " (NS mismatch)"}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {existingDomainInspection.status === "ns_mismatch" && (
                                            <div className="text-sm text-yellow-800 mt-2">
                                                Proceeding with Apply and Deploy confirms updating the domain name servers to this hosted zone.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Deployment Status */}
                        {(isDeploying || deploymentCompleted) && (
                            <div className="mb-3">
                                <label className="font-medium">Deployment Status</label>
                                <div className={`p-3 border-round mt-1 ${deploymentStatus === "COMPLETED" ? "bg-green-50 border-green-200" :
                                    deploymentStatus === "FAILED" ? "bg-red-50 border-red-200" :
                                        "bg-blue-50 border-blue-200"
                                    }`}>
                                    <div className="flex align-items-center mb-2">
                                        {deploymentStatus === "COMPLETED" && (
                                            <i className="pi pi-check-circle text-green-600 mr-2"></i>
                                        )}
                                        {deploymentStatus === "FAILED" && (
                                            <i className="pi pi-times-circle text-red-600 mr-2"></i>
                                        )}
                                        {deploymentStatus === "PROCESSING" && (
                                            <i className="pi pi-spin pi-spinner text-blue-600 mr-2"></i>
                                        )}
                                        <span className={`font-bold ${deploymentStatus === "COMPLETED" ? "text-green-700" :
                                            deploymentStatus === "FAILED" ? "text-red-700" : "text-blue-700"
                                            }`}>
                                            {deploymentStatus || "Checking status..."}
                                        </span>
                                    </div>

                                    {/* Show logs if status is COMPLETED or FAILED */}
                                    {(deploymentStatus === "COMPLETED" || deploymentStatus === "FAILED") && deploymentLogs ? (
                                        <div className="mt-3">
                                            <div className="font-medium mb-2">Deployment Logs:</div>
                                            <div className="bg-gray-900 text-green-400 p-3 border-round font-mono text-sm" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                <pre className="m-0 whitespace-pre-wrap">{deploymentLogs}</pre>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Show regular message for other statuses */
                                        deploymentMessage && (
                                            <div className={`text-sm ${deploymentStatus === "COMPLETED" ? "text-green-600" :
                                                deploymentStatus === "FAILED" ? "text-red-600" : "text-blue-600"
                                                }`}>
                                                {deploymentMessage}
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Deployment Info Message */}
                        {isDeploying && deploymentStatus === "PROCESSING" && (
                            <div className="mb-3">
                                <div className="flex align-items-center">
                                    <i className="pi pi-info-circle text-blue-600 mr-2"></i>
                                    <div className="text-sm">
                                        <div className="text-sm text-gray-600 mt-1 ml-1 mb-1">NOTE: You can close this dialog and come back at any time. An email will be sent to Wallist administrators when the deployment is completed.</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-row justify-content-end">
                            {/* Show Close button when deploying or deployment is complete */}
                            {(isDeploying || deploymentStatus === "COMPLETED" || deploymentStatus === "FAILED") ? (
                                <div className="flex">
                                    <Button
                                        label="Close"
                                        onClick={() => {
                                            hideAddCustomDomainDialog();
                                            refetch(); // Refresh the profiles list
                                        }}
                                        className="p-button-secondary"
                                        icon="pi pi-times"
                                    />
                                </div>
                            ) : (
                                /* Show Cancel and Apply buttons when not deploying */
                                <>
                                    <div className="flex mr-2">
                                        <Button label="Cancel" onClick={hideAddCustomDomainDialog} className="p-button-text p-button-danger" icon="pi pi-times" />
                                    </div>
                                    <div className="flex">
                                        <Button
                                            label="Apply and Deploy"
                                            onClick={applyCustomDomain}
                                            loading={isApplyingCustomDomain}
                                            icon="pi pi-check"
                                            disabled={!canApplyCustomDomain()}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Dialog>
            </div>
        </div>
    );
};

export default AllProfiles;
